<template>
  <section class="self-service-auth-page" id="self-service-auth-page">
    <div class="bg-shape bg-shape-one"></div>
    <div class="bg-shape bg-shape-two"></div>

    <main class="admin-main auth-main">
      <div class="auth-shell">
        <header class="auth-hero">
          <p class="eyebrow">Secure Access</p>
          <h1>
            <i class="fe fe-shield m-r-10"></i>
            Enrollee Verification
          </h1>
          <p class="subtitle">
            Enter your enrollee ID and verify with OTP to continue.
          </p>
        </header>

        <section>
          <div class="container px-0">
            <div class="row justify-content-center">
              <div class="col-lg-6 col-md-8">
                <div class="card auth-card m-b-30">
                  <div class="card-body">
                    <div class="section-heading">
                      <h2>Enrollee Access</h2>
                      <p>Enter your enrollee ID, request OTP, and continue to self service.</p>
                    </div>

                    <div class="form-group">
                      <label>Enrollee ID</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="id_card_number"
                        placeholder="Enter Enrollee ID"
                        :disabled="loading"
                      />

                      <button
                        type="button"
                        class="btn btn-outline-success m-t-10"
                        @click="requestOtp"
                        :disabled="loading || !id_card_number"
                      >
                        <i class="fe fe-send m-r-5"></i>
                        Request OTP
                      </button>
                    </div>

                    <div class="form-group m-b-20">
                      <label>OTP</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="otp"
                        placeholder="Enter OTP"
                        :disabled="loading"
                      />
                      <small class="text-muted d-block m-t-10">
                        Request OTP first, then enter the code sent to your registered contact.
                      </small>
                    </div>

                    <div class="row">
                      <div class="col-md-12 m-b-10">
                        <button
                          type="button"
                          class="btn auth-btn btn-block"
                          @click="verifyOtp"
                          :disabled="loading || !id_card_number || !otp"
                        >
                          <i class="fe fe-check-circle m-r-5"></i>
                          Verify & Continue
                        </button>
                      </div>
                    </div>

                    <p class="text-danger m-b-0" v-if="errorMessage">{{ errorMessage }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";

  export default {
    components: {
      Loading,
    },
    data() {
      return {
        id_card_number: "",
        otp: "",
        loading: false,
        errorMessage: "",
        isLoading: false,
        fullPage: true,
      };
    },
    methods: {
      requestOtp() {
        this.errorMessage = "";
        this.isLoading = true;

        this.axios
          .post("/api/v1/request/otp", {
            id_card_number: this.id_card_number,
          })
          .then(() => {
            this.$toasted.success("OTP sent successfully.", {
              position: "top-center",
              duration: 3000,
            });
          })
          .catch((error) => {
            this.handleError(
              error,
              "Unable to request OTP. Please check your details and try again."
            );
          })
          .finally(() => {
            this.isLoading = false;
          });
      },

      async verifyOtp() {
        this.errorMessage = "";
        this.isLoading = true;

        try {
          const response = await this.postWithFallback(
            ["/api/v1/verify/otp"],
            {
              id_card_number: this.id_card_number,
              otp: this.otp,
            }
          );

          this.$toasted.success("OTP verified successfully.", {
            position: "top-center",
            duration: 3000,
          });

          const data = response.data?.data || response.data;
          const token =
            data?.access_token ||
            data?.token ||
            response.data?.token ||
            response.data?.access_token;

          if (token) {
            // Signed in for this session like a normal login, so the self-service pages open.
            localStorage.setItem("jwt", token);
            if (data && data.id) localStorage.setItem("user", JSON.stringify(data));
            if (response.data && response.data.agency) localStorage.setItem("agency", JSON.stringify(response.data.agency));
            localStorage.setItem("token", token);
            this.axios.defaults.headers.common["Authorization"] =
              `Bearer ${token}`;
          }

          const enrolleeData = data?.enrollee || data;
          if (enrolleeData) {
            localStorage.setItem("enrollee", JSON.stringify(enrolleeData));
          }

          const payload = {
            enrolleeId: this.id_card_number,
            verifiedAt: Date.now(),
          };
          sessionStorage.setItem("self_service_access", JSON.stringify(payload));

          setTimeout(() => {
            window.location.href = "/self-service";
          }, 1500);
        } catch (error) {
          this.handleError(
            error,
            "Unable to verify OTP. Please check your code and try again."
          );
        } finally {
          this.isLoading = false;
        }
      },

      async postWithFallback(urls, payload) {
        let lastError = null;

        for (let index = 0; index < urls.length; index += 1) {
          try {
            const response = await this.axios.post(urls[index], payload);
            return response;
          } catch (error) {
            lastError = error;
            const status = error && error.response ? error.response.status : null;
            if (status && status !== 404) {
              throw error;
            }
          }
        }

        throw lastError || new Error("Request failed");
      },

      handleError(error, fallbackMessage) {
        const serverMessage =
          error &&
          error.response &&
          error.response.data &&
          (error.response.data.message ||
            error.response.data.error ||
            error.response.data);

        this.errorMessage = serverMessage || fallbackMessage;
        this.$toasted.error(this.errorMessage, {
          position: "top-center",
          duration: 3000,
        });
      },
    },
  };
</script>

<style scoped>
  @import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&display=swap");

  .self-service-auth-page {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    font-family: "Space Grotesk", sans-serif;
    background:
      radial-gradient(circle at 10% 15%, rgba(17, 105, 71, 0.15), transparent 45%),
      radial-gradient(circle at 90% 85%, rgba(0, 122, 204, 0.2), transparent 42%),
      linear-gradient(140deg, #f5f8f7 0%, #eef5ff 100%);
  }

  .auth-main {
    position: relative;
    z-index: 1;
    padding: 40px 20px;
  }

  .auth-shell {
    max-width: 1000px;
    margin: 0 auto;
  }

  .bg-shape {
    position: absolute;
    z-index: 0;
    border-radius: 50%;
    filter: blur(2px);
  }

  .bg-shape-one {
    top: -120px;
    right: -120px;
    width: 320px;
    height: 320px;
    background: rgba(0, 122, 204, 0.13);
  }

  .bg-shape-two {
    bottom: -100px;
    left: -100px;
    width: 280px;
    height: 280px;
    background: rgba(17, 105, 71, 0.16);
  }

  .auth-hero {
    text-align: center;
    margin-bottom: 24px;
  }

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #116947;
    font-weight: 700;
    font-size: 0.75rem;
    margin-bottom: 8px;
  }

  .auth-hero h1 {
    margin: 0;
    font-size: clamp(1.7rem, 4vw, 2.4rem);
    line-height: 1.1;
    color: #102a43;
  }

  .subtitle {
    max-width: 620px;
    margin: 12px auto 0;
    color: #334e68;
    font-size: 1rem;
  }

  .auth-card {
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(16, 42, 67, 0.08);
    border-radius: 20px;
    box-shadow: 0 18px 40px rgba(16, 42, 67, 0.12);
    backdrop-filter: blur(6px);
  }

  .section-heading {
    margin-bottom: 24px;
  }

  .section-heading h2 {
    font-size: 1.35rem;
    color: #102a43;
    margin-bottom: 6px;
  }

  .section-heading p {
    margin: 0;
    color: #486581;
  }

  .auth-btn {
    border: 0;
    border-radius: 12px;
    background: linear-gradient(120deg, #116947 0%, #007acc 100%);
    color: #fff;
    font-weight: 700;
  }

  .auth-btn:hover {
    color: #fff;
  }

  @media (max-width: 640px) {
    .auth-main {
      padding: 24px 14px;
    }
  }
</style>